import { RefObject, useEffect } from "react";

// An embed grabs focus while (or right after) it loads.
const LOAD_WINDOW_MS = 5000;
// Long enough to outlast the browser bringing the focused frame into view.
const RESTORE_WINDOW_MS = 1500;
const GESTURE_MS = 1000;

const isScrollable = (element: Element): boolean => {
  const { overflowY } = getComputedStyle(element);
  return (
    (overflowY === "auto" || overflowY === "scroll") &&
    element.scrollHeight > element.clientHeight
  );
};

const scrollAncestors = (node: Element): Element[] => {
  const ancestors: Element[] = [];
  for (let el = node.parentElement; el; el = el.parentElement) {
    if (isScrollable(el)) ancestors.push(el);
  }
  if (document.scrollingElement) ancestors.push(document.scrollingElement);
  return ancestors;
};

const scrollInstantly = (element: Element, top: number) => {
  element.scrollTo({ top, behavior: "instant" as ScrollBehavior });
};

/**
 * FS-3270: an embedded page that focuses one of its own fields while it loads
 * (a form's first input, an anchor) makes the browser scroll every ancestor to
 * bring the iframe into view — the Show Info page jumps to that section the
 * moment it opens. Browsers offer no attribute to stop an embed taking focus.
 *
 * A scroll that happens while focus sits in a frame that was off screen, just
 * loaded, and with no recent gesture from the viewer cannot be theirs: a click
 * needs the frame on screen, and their own scrolling or keyboard navigation
 * comes with a gesture. Such a scroll is undone and the frame gives focus back.
 */
export const useEmbedFocusScrollGuard = (
  containerRef: RefObject<HTMLElement>,
  content: string | undefined
): void => {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const iframes = Array.from(container.querySelectorAll("iframe"));
    if (!iframes.length) return undefined;

    const startedAt = Date.now();
    const loadedAt = new WeakMap<HTMLIFrameElement, number>();
    const onLoad = (event: Event) =>
      loadedAt.set(event.target as HTMLIFrameElement, Date.now());
    iframes.forEach((iframe) => iframe.addEventListener("load", onLoad));

    const lastScrollTop = new WeakMap<Element, number>();
    iframes.forEach((iframe) =>
      scrollAncestors(iframe).forEach((el) =>
        lastScrollTop.set(el, el.scrollTop)
      )
    );

    let lastGestureAt = 0;
    let isPointerDown = false;
    let restoreUntil = 0;
    let restoreTargets = new Map<Element, number>();

    const onGesture = () => {
      lastGestureAt = Date.now();
      restoreUntil = 0;
    };
    const onPointerDown = () => {
      isPointerDown = true;
      onGesture();
    };
    const onPointerUp = () => {
      isPointerDown = false;
    };

    // Where `iframe` sat before `scroller` moved: geometry, not an observer,
    // because the observer reports a frame later than this scroll event.
    const wasOutOfView = (iframe: Element, scroller: Element): boolean => {
      if (!scroller.contains(iframe)) return false;
      const moved = scroller.scrollTop - (lastScrollTop.get(scroller) ?? 0);
      const frame = iframe.getBoundingClientRect();
      const view =
        scroller === document.scrollingElement
          ? { top: 0, bottom: window.innerHeight }
          : scroller.getBoundingClientRect();
      return (
        frame.bottom + moved <= view.top || frame.top + moved >= view.bottom
      );
    };

    const isStolenFocus = (focused: Element | null, scroller: Element) => {
      if (!(focused instanceof HTMLIFrameElement)) return false;
      if (!iframes.includes(focused) || !wasOutOfView(focused, scroller)) {
        return false;
      }
      const now = Date.now();
      const loaded = loadedAt.get(focused) ?? startedAt;
      return (
        now - loaded < LOAD_WINDOW_MS &&
        now - lastGestureAt > GESTURE_MS &&
        !isPointerDown
      );
    };

    const onScroll = (event: Event) => {
      const target =
        event.target === document
          ? document.scrollingElement
          : (event.target as Element);
      if (!target) return;

      if (Date.now() < restoreUntil) {
        const top = restoreTargets.get(target);
        if (top !== undefined && target.scrollTop !== top) {
          scrollInstantly(target, top);
        }
        return;
      }

      const focused = document.activeElement;
      if (isStolenFocus(focused, target)) {
        restoreTargets = new Map(
          scrollAncestors(focused as Element).map((el) => [
            el,
            lastScrollTop.get(el) ?? 0,
          ])
        );
        restoreUntil = Date.now() + RESTORE_WINDOW_MS;
        restoreTargets.forEach((top, el) => scrollInstantly(el, top));
        (focused as HTMLIFrameElement).blur();
        return;
      }

      lastScrollTop.set(target, target.scrollTop);
    };

    document.addEventListener("scroll", onScroll, true);
    window.addEventListener("keydown", onGesture, true);
    window.addEventListener("wheel", onGesture, true);
    window.addEventListener("touchstart", onGesture, true);
    window.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("pointerup", onPointerUp, true);

    return () => {
      iframes.forEach((iframe) => iframe.removeEventListener("load", onLoad));
      document.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("keydown", onGesture, true);
      window.removeEventListener("wheel", onGesture, true);
      window.removeEventListener("touchstart", onGesture, true);
      window.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("pointerup", onPointerUp, true);
    };
  }, [containerRef, content]);
};
